import {Integration, INTEGRATION_TYPE} from '../models';

function trimUrl(urlString: string) {
  const url = new URL(urlString);
  return `${url.protocol}//${url.hostname}${url.port ? ':' + url.port : ''}`;
}

export const isUrl = (value: string) => {
  try {
    new URL(value);
    return true;
  } catch (e) {
    return false;
  }
}

export const isSftpHost = (value: string) => {
  const hostnameRegex = /^(?!:\/\/)([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]{1,63}(\.[a-zA-Z]{2,11})?$/;
  const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
  const ipv6Regex = /^([\da-f]{1,4}:){7}[\da-f]{1,4}$/i;

  if (hostnameRegex.test(value)) {
    return true;
  }
  if (ipv4Regex.test(value)) {
    return value.split('.').every(part => parseInt(part) <= 255);
  }
  if (ipv6Regex.test(value)) {
    return value.split(':').every(part => part.length <= 4);
  }
  return false;
}

export const isValidPort = (value: string) => {
  const portRegex = /^([0-9]{1,4}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])$/;
  return portRegex.test(value);
}

export function getSiteNameFromUrl(url: string): string {
  let site = /\/(site|t)\/(.*?)(\/|$)/.exec(url);
  return (site && site[2]) || '';
}

function dataToConfig(data: any, edit: boolean = false) {
  const {type} = data;
  let encrypted_config;

  switch (type) {
    case INTEGRATION_TYPE.CUSTOM_APP: {
      encrypted_config = data.encrypted_config;

      if (edit && !data.encrypted_config) {
        /*
         * If edit mode and no encrypted_config, then set encrypted_config to undefined
         *   to prevent encrypted_config from being updated in the database.
         */
        encrypted_config = undefined;
      } else {
        encrypted_config = JSON.stringify(JSON.parse(encrypted_config || '{}'));
      }

      return {
        config: JSON.stringify(JSON.parse(data.config)),
        encrypted_config,
      };
    }
    case INTEGRATION_TYPE.TABLEAU:
      if (!edit || (edit && data.pat_secret)) {
        // on create and on edit, only if `pat_secret` exists, then encrypt it
        encrypted_config = JSON.stringify({
          pat_secret: data.pat_secret,
        });
      }

      return {
        config: JSON.stringify({
          tableau_url: trimUrl(data.tableau_url),
          site_name: data.site_name,
          pat_name: data.pat_name,
          max_age: data.max_age,
          disable_verify_ssl: data.disable_verify_ssl,
          refresh_token: data.refresh_token,
          version: data.version || null,
        }),
        encrypted_config,
      };
    case INTEGRATION_TYPE.SLACK:
      const isWebhook = !!data.webhook;
      const isAccessToken = !!data.access_token;

      if (edit && !data.webhook && !data.access_token) {
        return {};
      }

      const config = {
        connection_type: isWebhook ? 'webhook' : 'access_token',
        ...(isWebhook && {source: data.webhook}),
      };
      if (isAccessToken) {
        encrypted_config = JSON.stringify({
          source: data.access_token,
        });
      }
      return {
        config: JSON.stringify(config),
        encrypted_config,
      };
    case INTEGRATION_TYPE.OPEN_AI:
      if (!edit || (edit && data.api_key)) {
        encrypted_config = JSON.stringify({
          api_key: data.api_key,
        });
      }

      return {
        config: JSON.stringify({}),
        encrypted_config,
      };
    case INTEGRATION_TYPE.GOOGLE_CHAT:
      return {
        config: JSON.stringify({
          webhooks: data.webhooks,
        }),
      };
    case INTEGRATION_TYPE.MS_TEAMS:
      return {
        config: JSON.stringify({
          webhooks: data.webhooks,
        }),
      };
    case INTEGRATION_TYPE.SFTP:
      if (!edit || (edit && data.password) || (edit && data.ssh_private_key) || (edit && data.ssh_passphrase)) {
        encrypted_config = JSON.stringify({
          ...(data.password ? {password: data.password} : {}),
          ...(data.ssh_private_key ? {ssh_private_key: data.ssh_private_key} : {}),
          ...(data.ssh_passphrase ? {ssh_passphrase: data.ssh_passphrase} : {}),
        });
      }

      return {
        config: JSON.stringify({
          host: data.host,
          port: data.port,
          username: data.username,
        }),
        encrypted_config,
      };
    case INTEGRATION_TYPE.AWS_S3:
      if (!edit || (edit && data.secret)) {
        encrypted_config = JSON.stringify({
          secret: data.secret,
        });
      }

      return {
        config: JSON.stringify({
          name: data.name,
          bucket_name: data.bucket_name,
          key: data.key,
          region: data.region,
        }),
        encrypted_config,
      };
    case INTEGRATION_TYPE.GDRIVE:
      if (!edit || (edit && data.service_account_json)) {
        encrypted_config = JSON.stringify({
          service_account_json: data.service_account_json,
        });
      }

      return {
        config: JSON.stringify({
          name: data.name,
        }),
        encrypted_config,
      };
    case INTEGRATION_TYPE.SMTP:
      if (!edit || (edit && data.password)) {
        encrypted_config = JSON.stringify({
          password: data.password,
        });
      }

      return {
        config: JSON.stringify({
          host: data.host,
          port: data.port,
          username: data.username,
          email_addresses: data.email_addresses,
          attachment_size: data.attachment_size,
          use_tls: data.use_tls
        }),
        encrypted_config,
      };
    default:
      return {};
  }
}

export function fromDataToIntegration(data: any, edit: boolean = false) {
  /*
   * convert form data to integration entity
   */
  return {
    name: data.name,
    type: data.type,
    ...dataToConfig(data, edit),
  };
}

export function fromIntegrationToData(integration: Integration) {
  /*
   * convert integration entity to form data
   */
  return {
    uid: integration.uid || '',
    name: integration.name,
    type: integration.type,
    config_json: {
      ...JSON.parse(integration.config || '{}'),
    },
    created_by: integration.created_by,
    created_on_utc: integration.created_on_utc,
    changed_by: integration.changed_by,
    changed_on_utc: integration.changed_on_utc,
    reports: integration.reports,
    sharing_settings: integration.sharing_settings,
  };
}
